import * as angular from "angular";
import { FORM_ENTITIES } from "../../../routes";
import {
	JsonSchemaDefinition,
	FormUiSchema,
	isRecord,
	referenceOptions,
	ReferenceOptions
} from "../runtime";
import template from "./reference.html";
import type { FormTimeSettings } from "./time-zone-context";
import type { TimeZoneContext } from "../TimeZoneContext";
import choiceControl from "./choice-control";
import tieredSelect from "../../../components/tiered-select/tiered-select";
import confirmClear from "../../../behaviors/confirm-clear";
import {
	referenceLabelKey,
	ReferenceLabelStore
} from "./reference-label-store";

interface ReferenceField {
	Key: {
		Code: string;
	};
	Data?: Record<string, unknown>[];
	IsCodeHidden?: boolean;
	IsPrimary?: boolean;
	IsCodeSystemManaged?: boolean;
	RenderingDirectives?: {
		Display?: {
			List?: unknown;
		};
	};
}

interface ReferenceAppState {
	forms: {
		selected?: FormTimeSettings & {
			Id: number;
			Fields?: ReferenceField[];
			Agency?: { Types?: Record<string, unknown>[] };
		};
	};
}

interface ReferenceApi {
	post(url: string, payload: unknown): PromiseLike<unknown>;
}

interface DisplayHelpers {
	getRenderingSlots(item: Record<string, unknown>, definitions: unknown[]): { data?: unknown }[];
}

interface EvaluationScope {
	$evalAsync(callback: () => void): void;
}

interface ReferenceChoice {
	value: number | null;
	label: string;
	timeZone?: string;
}

interface NumericReferenceChoice {
	value: number;
	label: string;
	timeZone?: string;
}

interface EntitySelection {
	friendly: Record<string, unknown>;
	primary: Record<string, unknown>;
}

interface EntitySelectBox {
	setValue(primary: Record<string, unknown>, friendly: Record<string, unknown>): void;
	clearSelected(): void;
	switchPrimary(): void;
}

interface ReferenceSource {
	choices: ReferenceChoice[];
	entities?: Record<string, unknown>[];
	configuration?: unknown;
}

function records(value: unknown): Record<string, unknown>[] {
	return Array.isArray(value) ? value.filter(isRecord) : [];
}

function textValue(value: unknown, fallback: string): string {
	return typeof value === "string" && value.trim() ? value : fallback;
}

function propertyValue(value: unknown, key: string): unknown {
	return isRecord(value) ? value[key] ?? value[key[0].toUpperCase() + key.slice(1)] : undefined;
}

function hidesCode(value: unknown): boolean {
	return propertyValue(value, "isCodeHidden") === true || propertyValue(value, "isCodeSystemManaged") === true;
}

function entityLabel(item: Record<string, unknown>, includeCode: boolean): string {
	const name = textValue(item.Name, "");
	const code = textValue(item.Code, "");

	return includeCode && name && code ? name + " (" + code + ")" : name || code || "Entity";
}

export function toApiConfiguration(value: unknown): unknown {
	if(Array.isArray(value)) {
		return value.map(toApiConfiguration);
	}

	if(isRecord(value)) {
		const configuration: Record<string, unknown> = {};

		for(const key of Object.keys(value)) {
			const apiKey = key[0].toUpperCase() + key.slice(1);
			configuration[apiKey] = toApiConfiguration(value[key]);
		}

		return configuration;
	}

	return value;
}

export function referenceValue(item: unknown, property = "id"): number | null {
	if(!isRecord(item)) {
		return null;
	}

	const value = item[property] ?? item[property[0].toUpperCase() + property.slice(1)];

	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export interface ReferenceController {
	schema: JsonSchemaDefinition;
	ui: FormUiSchema;
	value?: number | null;
	enabled: boolean;
	title: string;
	inputId: string;
	required?: boolean;
	invalid?: boolean;
	describedBy?: string;
	onChange(args: { value: number | undefined }): void;
	onTimeZoneChange?: (args: { value?: string }) => void;
	labelStore?: ReferenceLabelStore;
	contextKey?: unknown;
	timeZoneContext?: TimeZoneContext;
}

// AngularJS assigns bindings before constructing the controller.
// eslint-disable-next-line no-redeclare
export class ReferenceController implements angular.IController {
	public static $inject = ["$scope", "appState", "api", "uiHelpers"];
	public choices: NumericReferenceChoice[] = [];

	public error?: string;

	public loading = false;

	public entities: Record<string, unknown>[] = [];

	public configuration?: unknown;

	public selectedEntity?: EntitySelection;

	private generation = 0;

	private entitySelect?: EntitySelectBox;

	private synchronizing = false;

	public constructor(
		private $scope: EvaluationScope,
		private appState: ReferenceAppState,
		private api: ReferenceApi,
		private uiHelpers: DisplayHelpers
	) {}

	public $onChanges(changes: angular.IOnChangesObject): void {
		this.enabled = this.enabled ?? true;
		this.title = this.title ?? "";
		if(changes.ui || changes.schema || changes.contextKey) {
			void this.load();
		}

		if(changes.value || changes.timeZoneContext) {
			this.synchronizeEntity();
			this.publishTimeZone(changes.value?.previousValue != null && this.value == null);
		}
	}

	public $onDestroy(): void {
		this.generation++;
	}

	public change(value?: number | null): void {
		if(this.enabled !== false) {
			this.value = value;
			this.synchronizeEntity();
			this.onChange({ value: this.value ?? undefined });
			this.publishTimeZone(true);
		}
	}

	public retry(): void {
		void this.load();
	}

	public entityChanged = (selection?: EntitySelection): void => {
		if(this.synchronizing || !this.enabled || this.loading || this.error) {
			return;
		}

		const options = referenceOptions(this.schema, this.ui);
		const self = propertyValue(propertyValue(this.configuration, "relationshipType"), "code") === "SELF";
		const subject = self ? selection?.friendly : selection?.primary;
		this.change(referenceValue(subject, options.modelProperty));
	};

	public setEntitySelect(selectBox: EntitySelectBox): void {
		this.entitySelect = selectBox;
		this.synchronizeEntity();
	}

	public showPrimarySwitcher(): boolean {
		return records(this.selectedEntity?.friendly.Entities).length > 1;
	}

	public switchPrimary(): void {
		if(this.enabled && !this.loading && !this.error) {
			this.entitySelect?.switchPrimary();
		}
	}

	private synchronizeEntity(): void {
		const options = referenceOptions(this.schema, this.ui);
		const self = propertyValue(propertyValue(this.configuration, "relationshipType"), "code") === "SELF";
		this.selectedEntity = undefined;

		for(const friendly of this.entities) {
			const primary = self ? friendly : records(friendly.Entities).map(link => link.Entity)
				.find(entity => this.value != null && referenceValue(entity, options.modelProperty) === this.value);

			if(this.value != null && referenceValue(primary, options.modelProperty) === this.value) {
				this.selectedEntity = { friendly, primary: self ? {} : primary as Record<string, unknown> };
				break;
			}
		}

		if(this.entitySelect) {
			this.synchronizing = true;

			try {
				this.entitySelect.clearSelected();
				this.entitySelect.setValue(this.selectedEntity?.primary ?? {}, this.selectedEntity?.friendly ?? {});
			}finally {
				this.synchronizing = false;
			}
		}
	}

	private async load(): Promise<void> {
		const generation = ++this.generation;
		const options = referenceOptions(this.schema, this.ui);
		this.error = undefined;
		this.loading = true;
		this.entitySelect = undefined;

		try {
			const source: ReferenceSource = this.schema.format === "datapoint"
				? { choices: this.datapointChoices(options) }
				: await this.entityChoices(options);

			if(generation === this.generation) {
				this.$scope.$evalAsync(() => {
					if(generation !== this.generation) {
						return;
					}

					const valid = source.choices.filter(
						(choice): choice is NumericReferenceChoice => typeof choice.value === "number"
					);

					this.choices = [...new Map(valid.map(choice => [choice.value, choice])).values()];
					this.loading = false;
					this.entities = source.entities ?? [];
					this.configuration = source.configuration;
					this.synchronizeEntity();
					this.labelStore?.set(referenceLabelKey(this.schema, this.ui), new Map(this.choices.map(choice => [choice.value, choice.label])));
					this.publishTimeZone();
				});
			}
		}catch(error) {
			if(generation === this.generation) {
				this.$scope.$evalAsync(() => {
					if(generation !== this.generation) {
						return;
					}

					this.choices = [];
					this.loading = false;
					this.error = "The choices could not be loaded. Please try again.";
				});
			}
		}
	}

	private datapointChoices(options: ReferenceOptions): ReferenceChoice[] {
		const field = this.appState.forms.selected?.Fields?.find(item => item.Key.Code === options.fieldCode);

		if(!field) {
			throw new Error("Missing datapoint source");
		}

		const hideCode = hidesCode(field);
		const definitions = field.RenderingDirectives?.Display?.List;

		return (field.Data ?? []).filter(item => item.IsActive !== false).map(item => {
			const safe = this.displayEntity(item, hideCode, "Unnamed item");
			// Array slots preserve authored text without the report helper's secondary-slot parentheses.
			const slots = Array.isArray(definitions)
				? this.uiHelpers.getRenderingSlots(safe, Array.from({ length: 4 }, (_, index) => {
					const slot = definitions[index];

					return slot == null ? [] : Array.isArray(slot) ? slot : [slot];
				}))
					.map(slot => slot.data)
				: [textValue(safe.Name, "") || safe.Code, textValue(safe.Name, "") && safe.Code];

			return {
				value: referenceValue(item, options.modelProperty),
				label: slots.filter(Boolean).join(" — ") || entityLabel(safe, true)
			};
		});
	}

	private async entityChoices(options: ReferenceOptions): Promise<ReferenceSource> {
		const form = this.appState.forms.selected;

		if(!form?.Id || !options.configuration) {
			throw new Error("Missing entity source");
		}

		const result = await this.api.post(FORM_ENTITIES(form.Id), [toApiConfiguration(options.configuration)]);
		const relationshipType = options.configuration.relationshipType ?? options.configuration.RelationshipType;
		const self = isRecord(relationshipType) && (relationshipType.code ?? relationshipType.Code) === "SELF";
		const firstResult = records(result)[0];
		const field = form.Fields?.find(item => options.fieldCode ? item.Key.Code === options.fieldCode : item.IsPrimary);
		const hideCode = hidesCode(field) || hidesCode(options.configuration);
		const safe = (item: Record<string, unknown>, role: "friendly" | "primary") => {
			const configuredType = propertyValue(propertyValue(options.configuration, role), "type");
			const code = propertyValue(item.Type, "code") ?? propertyValue(configuredType, "code");
			const type = form.Agency?.Types?.find(candidate => candidate.Code === code);

			return this.displayEntity(item, hideCode || hidesCode(type) || hidesCode(configuredType), "Entity");
		};

		const entities: Record<string, unknown>[] = records(firstResult?.Entities)
			.filter(item => item.IsActive !== false)
			.map(item => ({
				...safe(item, "friendly"),
				...(self ? {} : {
					Entities: records(item.Entities).filter(link => isRecord(link.Entity) && link.Entity.IsActive !== false)
						.map(link => ({ ...link, Entity: safe(link.Entity as Record<string, unknown>, "primary") }))
				})
			}));
		const choices = entities
			.flatMap(item => {
				const entries = self ? [item] : records(item.Entities).map(link => link.Entity)
					.filter(isRecord);

				return entries
					.filter(entity => entity.IsActive !== false)
					.map(entity => ({
						value: referenceValue(entity, options.modelProperty),
						timeZone: isRecord(entity.TimeZone) && typeof entity.TimeZone.Code === "string"
							? entity.TimeZone.Code
							: undefined,
						label: self
							? entityLabel(safe(item, "friendly"), true)
							: entityLabel(safe(item, "friendly"), true) + " — " + entityLabel(safe(entity, "primary"), false)
					}));
			});

		return { choices, entities, configuration: toApiConfiguration(options.configuration) };
	}

	private displayEntity(item: Record<string, unknown>, hideCode: boolean, fallback: string): Record<string, unknown> {
		const type = isRecord(item.Type) ? item.Type : undefined;

		if(hideCode || hidesCode(type)) {
			return {
				...item,
				Code: undefined,
				Name: textValue(item.Name, "") || fallback
			};
		}

		return item;
	}

	private publishTimeZone(cleared = false): void {
		if(this.schema?.format !== "entity") {
			return;
		}

		const choice = this.choices.find(item => item.value === this.value);

		if(choice || (cleared && this.value == null)) {
			this.onTimeZoneChange?.({ value: choice?.timeZone });
		}
	}
}

export default angular.module("vesl.extensions.json-schema.reference", [choiceControl, tieredSelect, confirmClear])
	.component("jsonSchemaReference", {
		template,
		controller: ReferenceController,
		bindings: {
			schema: "<",
			ui: "<",
			value: "<",
			enabled: "<",
			title: "<fieldTitle",
			inputId: "<",
			required: "<?",
			invalid: "<?",
			describedBy: "<?",
			timeZoneContext: "<?",
			onTimeZoneChange: "&?",
			labelStore: "<?",
			onChange: "&"
		}
	})
	.name;
